import { useEffect, useMemo, useRef, useState } from 'react';
import { WorkflowProps } from '@/types/workflow';
import {
	normalizePaletteState,
	PaletteAcousticIntent,
	PaletteDaylightViewsAssumption,
	PaletteDurabilityComfortBias,
	PaletteInputs,
	PaletteIntendedUse,
	PaletteMaterialCharacter,
	PaletteWellnessIntent,
	WorkflowStage,
} from '@/types/palette';
import { Select } from '@/components/UI/Select/Select';
import { WeaveButton } from '@/components/UI/WeaveButton/WeaveButton';
import { WeaveInput } from '@/components/UI/WeaveInput/WeaveInput';
import { computePaletteBaselineScore, createPaletteDefinition } from '@/services/score/palette';
import { RoomData, SpaceData } from '@/components/SpacePlanningPanel/SpacePlanningTypes';
import { newModal, WeaveModalElement } from '@/components/UI/WeaveModal/WeaveModal';
import styles from './styles.module.scss';

const DEFAULT_INPUTS: PaletteInputs = {
	intendedUse: 'office_commercial',
	wellnessIntent: 'above_baseline',
	materialCharacter: 'mixed_synthetic_natural',
	daylightViewsAssumption: 'moderate_daylight_access',
	acousticIntent: 'balanced',
	durabilityComfortBias: 'balanced',
};

const INTENDED_USE_OPTIONS: { label: string; value: PaletteIntendedUse }[] = [
	{ label: 'Office / Commercial', value: 'office_commercial' },
	{ label: 'Healthcare', value: 'healthcare' },
	{ label: 'K-12 Education', value: 'k12_education' },
	{ label: 'Higher Education', value: 'higher_education' },
	{ label: 'Residential', value: 'residential' },
	{ label: 'Civic / Community', value: 'civic_community' },
	{ label: 'Laboratory / Industrial', value: 'laboratory_industrial' },
	{ label: 'Other', value: 'other' },
];

const WELLNESS_INTENT_OPTIONS: { label: string; value: PaletteWellnessIntent }[] = [
	{ label: 'Code Minimum / Baseline', value: 'code_minimum_baseline' },
	{ label: 'Above Baseline', value: 'above_baseline' },
	{ label: 'Wellness-Focused', value: 'wellness_focused' },
	{ label: 'Biophilic / Regenerative', value: 'biophilic_regenerative' },
];

const MATERIAL_CHARACTER_OPTIONS: { label: string; value: PaletteMaterialCharacter }[] = [
	{ label: 'Predominantly Synthetic', value: 'predominantly_synthetic' },
	{ label: 'Mixed Synthetic + Natural', value: 'mixed_synthetic_natural' },
	{ label: 'Predominantly Natural', value: 'predominantly_natural' },
	{ label: 'Exposed Structure / Minimal Finishes', value: 'exposed_structure_minimal_finishes' },
];

const DAYLIGHT_VIEWS_OPTIONS: { label: string; value: PaletteDaylightViewsAssumption }[] = [
	{ label: 'Limited daylight / controlled', value: 'limited_daylight_controlled' },
	{ label: 'Moderate daylight access', value: 'moderate_daylight_access' },
	{ label: 'High daylight w/ glare control', value: 'high_daylight_with_glare_control' },
	{ label: 'Daylight + intentional views to nature', value: 'daylight_intentional_views_nature' },
];

const ACOUSTIC_INTENT_OPTIONS: { label: string; value: PaletteAcousticIntent }[] = [
	{ label: 'Performance-driven / noise tolerant', value: 'performance_driven_noise_tolerant' },
	{ label: 'Balanced', value: 'balanced' },
	{ label: 'Acoustically calm / restorative', value: 'acoustically_calm_restorative' },
];

const DURABILITY_COMFORT_OPTIONS: { label: string; value: PaletteDurabilityComfortBias }[] = [
	{ label: 'Durability-first', value: 'durability_first' },
	{ label: 'Balanced', value: 'balanced' },
	{ label: 'Comfort / experience-first', value: 'comfort_experience_first' },
];

const getPaletteOptions = (palettes: { id: string; name: string }[]) => [
	{ label: 'Unassigned', value: '' },
	...palettes.map((palette) => ({ label: palette.name, value: palette.id })),
];

type SpacesWithRooms = Record<string, SpaceData & { rooms: RoomData[] }>;
type FunctionAssignmentItem = {
	key: string;
	label: string;
};
type AreaAssignmentItem = {
	key: string;
	legacyKey: string;
	label: string;
};

const buildAreaAssignmentKey = (
	spaceKey: string,
	room: Pick<RoomData, 'spaceDescription' | 'level'>
) =>
	room.spaceDescription
		? typeof room.level === 'number'
			? `${spaceKey}::L${room.level}::${room.spaceDescription}`
			: `${spaceKey}::${room.spaceDescription}`
		: '';

const STAGE_GUIDANCE: Record<WorkflowStage, string> = {
	predesign_programming:
		'Use broad palettes and function-level assignments to establish early proxy assumptions.',
	schematic: 'Refine with area assignments and validate palette coverage for key spaces.',
	design_development:
		'Use palette assignments as defaults only; prioritize explicit room material selections.',
};

const STAGE_BEHAVIOR: Record<
	WorkflowStage,
	{
		createPaletteHint: string;
		functionHint: string;
		areaHint: string;
		showAreaAssignments: boolean;
	}
> = {
	predesign_programming: {
		createPaletteHint: 'Create high-level palettes intended for function-level proxy scoring.',
		functionHint:
			'Assign palettes by function. This is the primary assignment level in this stage.',
		areaHint: 'Area assignments become available in Gate 2.',
		showAreaAssignments: false,
	},
	schematic: {
		createPaletteHint:
			'Refine or create palettes and use them for function + area-level assumptions.',
		functionHint:
			'Function assignments still provide defaults where area assignments are missing.',
		areaHint: 'Assign palettes by area to refine assumptions beyond function-level defaults.',
		showAreaAssignments: true,
	},
	design_development: {
		createPaletteHint:
			'Palettes remain useful as defaults, but explicit room materials should drive final outcomes.',
		functionHint:
			'Function assignments are fallback defaults only in this stage when detailed inputs are missing.',
		areaHint:
			'Area assignments can still provide fallback defaults, but explicit room materials take precedence.',
		showAreaAssignments: true,
	},
};

type CreatePalettePayload = {
	name: string;
	inputs: PaletteInputs;
};

const getScoreIndicator = (score: number) => {
	if (score < 5) {
		return { label: 'Low', className: styles.scoreIndicatorLow };
	}
	if (score < 7) {
		return { label: 'Medium', className: styles.scoreIndicatorMedium };
	}
	return { label: 'High', className: styles.scoreIndicatorHigh };
};

const CreatePaletteModalContent = ({
	onCreate,
	onCancel,
}: {
	onCreate: (payload: CreatePalettePayload) => boolean;
	onCancel: () => void;
}) => {
	const [paletteName, setPaletteName] = useState('');
	const [paletteInputs, setPaletteInputs] = useState<PaletteInputs>(DEFAULT_INPUTS);
	const previewScore = useMemo(() => computePaletteBaselineScore(paletteInputs), [paletteInputs]);

	return (
		<div className={styles.modalContent}>
			<div className={styles.field}>
				<label>Palette name</label>
				<WeaveInput
					type="text"
					value={paletteName}
					onChange={(event: unknown) => {
						const target = event as { nativeEvent: { detail: { value: string } } };
						setPaletteName(target.nativeEvent.detail.value);
					}}
				/>
			</div>

			<div className={styles.inputsGrid}>
				<div className={styles.field}>
					<label>Intended use</label>
					<Select
						options={INTENDED_USE_OPTIONS}
						value={paletteInputs.intendedUse}
						onChange={(event) =>
							setPaletteInputs((prev) => ({
								...prev,
								intendedUse: event.detail.value as PaletteIntendedUse,
							}))
						}
					/>
				</div>
				<div className={styles.field}>
					<label>Sustainability / wellness intent</label>
					<Select
						options={WELLNESS_INTENT_OPTIONS}
						value={paletteInputs.wellnessIntent}
						onChange={(event) =>
							setPaletteInputs((prev) => ({
								...prev,
								wellnessIntent: event.detail.value as PaletteWellnessIntent,
							}))
						}
					/>
				</div>
				<div className={styles.field}>
					<label>Material character</label>
					<Select
						options={MATERIAL_CHARACTER_OPTIONS}
						value={paletteInputs.materialCharacter}
						onChange={(event) =>
							setPaletteInputs((prev) => ({
								...prev,
								materialCharacter: event.detail.value as PaletteMaterialCharacter,
							}))
						}
					/>
				</div>
				<div className={styles.field}>
					<label>Daylight / views assumption</label>
					<Select
						options={DAYLIGHT_VIEWS_OPTIONS}
						value={paletteInputs.daylightViewsAssumption}
						onChange={(event) =>
							setPaletteInputs((prev) => ({
								...prev,
								daylightViewsAssumption: event.detail
									.value as PaletteDaylightViewsAssumption,
							}))
						}
					/>
				</div>
				<div className={styles.field}>
					<label>Acoustic intent</label>
					<Select
						options={ACOUSTIC_INTENT_OPTIONS}
						value={paletteInputs.acousticIntent}
						onChange={(event) =>
							setPaletteInputs((prev) => ({
								...prev,
								acousticIntent: event.detail.value as PaletteAcousticIntent,
							}))
						}
					/>
				</div>
				<div className={styles.field}>
					<label>Durability vs comfort bias</label>
					<Select
						options={DURABILITY_COMFORT_OPTIONS}
						value={paletteInputs.durabilityComfortBias}
						onChange={(event) =>
							setPaletteInputs((prev) => ({
								...prev,
								durabilityComfortBias: event.detail
									.value as PaletteDurabilityComfortBias,
							}))
						}
					/>
				</div>
			</div>

			<div className={styles.preview}>
				<div>
					<strong>Generated baseline score:</strong> {previewScore.clampedScore}
				</div>
				<div className={styles.previewText}>{previewScore.explanation}</div>
				<div className={styles.previewNote}>
					This is a starting assumption for early-stage benchmarking, not a final project
					BioScore.
				</div>
			</div>

			<div className={styles.modalActions}>
				<WeaveButton variant="outlined" onClick={onCancel}>
					Cancel
				</WeaveButton>
				<WeaveButton
					variant="solid"
					onClick={() => {
						const created = onCreate({
							name: paletteName,
							inputs: paletteInputs,
						});
						if (created) {
							onCancel();
						}
					}}
					disabled={!paletteName.trim()}
				>
					Create palette
				</WeaveButton>
			</div>
		</div>
	);
};

const MaterialsAssumptions: React.FC<WorkflowProps> = ({
	onDataChange,
	workflowData,
	activeSection,
	currentStage,
}) => {
	const normalizedInitialState = normalizePaletteState(workflowData[activeSection]?.data);
	const [paletteState, setPaletteState] = useState(normalizedInitialState);
	const [editingPaletteId, setEditingPaletteId] = useState<string | null>(null);
	const [editedPaletteName, setEditedPaletteName] = useState('');
	const lastWorkflowSnapshotRef = useRef('');
	const normalizedWorkflowPaletteState = useMemo(
		() => normalizePaletteState(workflowData[activeSection]?.data),
		[workflowData, activeSection]
	);
	const workflowSnapshot = useMemo(
		() => JSON.stringify(normalizedWorkflowPaletteState),
		[normalizedWorkflowPaletteState]
	);
	const localSnapshot = useMemo(() => JSON.stringify(paletteState), [paletteState]);
	const effectiveStage = currentStage ?? paletteState.stage;
	const stageBehavior = STAGE_BEHAVIOR[effectiveStage];

	useEffect(() => {
		// Pull from store only when workflow payload itself actually changed.
		if (lastWorkflowSnapshotRef.current !== workflowSnapshot) {
			lastWorkflowSnapshotRef.current = workflowSnapshot;
			if (workflowSnapshot !== localSnapshot) {
				setPaletteState(normalizedWorkflowPaletteState);
			}
		}
	}, [workflowSnapshot, localSnapshot, normalizedWorkflowPaletteState]);

	useEffect(() => {
		// Push local edits to store.
		if (workflowSnapshot !== localSnapshot) {
			onDataChange({ data: paletteState });
		}
	}, [workflowSnapshot, localSnapshot, paletteState, onDataChange]);

	useEffect(() => {
		if (!currentStage || paletteState.stage === currentStage) return;
		setPaletteState((prev) => ({
			...prev,
			stage: currentStage,
			updatedAt: new Date().toISOString(),
		}));
	}, [currentStage, paletteState.stage]);

	const functionAssignments = useMemo<FunctionAssignmentItem[]>(() => {
		const spacesData = workflowData[2]?.data as SpacesWithRooms | undefined;
		if (!spacesData) return [];
		return Object.entries(spacesData).map(([functionKey, space]) => ({
			key: functionKey,
			label: space.name || functionKey,
		}));
	}, [workflowData]);

	const areaAssignments = useMemo<AreaAssignmentItem[]>(() => {
		const spacesData = workflowData[2]?.data as SpacesWithRooms | undefined;
		if (!spacesData) return [];
		const map = new Map<string, AreaAssignmentItem>();
		Object.entries(spacesData).forEach(([spaceKey, space]) => {
			space.rooms?.forEach((room) => {
				if (room.spaceDescription) {
					const areaKey = buildAreaAssignmentKey(spaceKey, room);
					const legacyKey = `${spaceKey}::${room.spaceDescription}`;
					if (!map.has(areaKey)) {
						const labelPrefix = space.name || spaceKey;
						const levelLabel =
							typeof room.level === 'number' ? `Level ${room.level} - ` : '';
						map.set(areaKey, {
							key: areaKey,
							legacyKey,
							label: `${levelLabel}${room.spaceDescription} (${labelPrefix})`,
						});
					}
				}
			});
		});
		return Array.from(map.values());
	}, [workflowData]);

	const handleCreatePalette = ({ name, inputs }: CreatePalettePayload): boolean => {
		const normalizedName = name.trim();
		if (!normalizedName) return false;

		const now = new Date().toISOString();
		const nextPalette = createPaletteDefinition(
			{
				id: `palette-${crypto.randomUUID()}`,
				name: normalizedName,
				inputs,
			},
			now
		);

		setPaletteState((prev) => ({
			...prev,
			palettes: [...prev.palettes, nextPalette],
			updatedAt: now,
		}));

		return true;
	};

	const startPaletteRename = (paletteId: string, currentName: string) => {
		setEditingPaletteId(paletteId);
		setEditedPaletteName(currentName);
	};

	const cancelPaletteRename = () => {
		setEditingPaletteId(null);
		setEditedPaletteName('');
	};

	const savePaletteRename = () => {
		if (!editingPaletteId) return;
		const normalizedName = editedPaletteName.trim();
		if (!normalizedName) {
			cancelPaletteRename();
			return;
		}

		setPaletteState((prev) => ({
			...prev,
			palettes: prev.palettes.map((palette) =>
				palette.id === editingPaletteId ? { ...palette, name: normalizedName } : palette
			),
			updatedAt: new Date().toISOString(),
		}));
		cancelPaletteRename();
	};

	const closeCreatePaletteModal = () => {
		const existingModal = document.querySelector('weave-modal') as WeaveModalElement | null;
		existingModal?.close();
	};

	const openCreatePaletteModal = () => {
		newModal({
			title: 'Create Palette',
			width: '900px',
			content: (
				<CreatePaletteModalContent
					onCreate={handleCreatePalette}
					onCancel={closeCreatePaletteModal}
				/>
			),
		});
	};

	const updateFunctionAssignment = (functionKey: string, paletteId: string) => {
		setPaletteState((prev) => {
			const nextAssignments = { ...prev.functionAssignments };
			if (!paletteId) {
				delete nextAssignments[functionKey];
			} else {
				nextAssignments[functionKey] = paletteId;
			}

			return {
				...prev,
				functionAssignments: nextAssignments,
				updatedAt: new Date().toISOString(),
			};
		});
	};

	const updateAreaAssignment = (areaKey: string, paletteId: string) => {
		setPaletteState((prev) => {
			const nextAssignments = { ...prev.typologyAssignments };
			if (!paletteId) {
				delete nextAssignments[areaKey];
			} else {
				nextAssignments[areaKey] = paletteId;
			}

			return {
				...prev,
				typologyAssignments: nextAssignments,
				updatedAt: new Date().toISOString(),
			};
		});
	};

	const handleDeletePalette = (paletteId: string) => {
		const functionUsageCount = Object.values(paletteState.functionAssignments).filter(
			(value) => value === paletteId
		).length;
		const areaUsageCount = Object.values(paletteState.typologyAssignments).filter(
			(value) => value === paletteId
		).length;
		const totalUsage = functionUsageCount + areaUsageCount;

		if (totalUsage > 0) {
			const confirmed = window.confirm(
				`This palette is currently assigned ${totalUsage} time(s) (${functionUsageCount} function, ${areaUsageCount} area). Delete and unassign all references?`
			);
			if (!confirmed) return;
		}

		setPaletteState((prev) => {
			const nextFunctionAssignments = Object.fromEntries(
				Object.entries(prev.functionAssignments).filter(([, value]) => value !== paletteId)
			);
			const nextAreaAssignments = Object.fromEntries(
				Object.entries(prev.typologyAssignments).filter(([, value]) => value !== paletteId)
			);

			return {
				...prev,
				palettes: prev.palettes.filter((palette) => palette.id !== paletteId),
				functionAssignments: nextFunctionAssignments,
				typologyAssignments: nextAreaAssignments,
				updatedAt: new Date().toISOString(),
			};
		});
	};

	return (
		<div className={styles.container}>
			<div className={styles.instructions}>
				<h2 className={styles.title}>Materials & Assumptions</h2>
				<p className={styles.subtitle}>
					Create early-stage material palettes as proxy scoring inputs. Explicit room
					materials still override these assumptions later.
				</p>
			</div>

			<div className={styles.card}>
				<h3 className={styles.cardTitle}>Project Stage</h3>
				<p className={styles.stageGuidance}>{STAGE_GUIDANCE[effectiveStage]}</p>
			</div>

			<div className={styles.card}>
				<div className={styles.cardHeaderRow}>
					<h3 className={styles.cardTitle}>Create Palette</h3>
					<WeaveButton variant="solid" onClick={openCreatePaletteModal}>
						New palette
					</WeaveButton>
				</div>
				<p className={styles.cardDescription}>{stageBehavior.createPaletteHint}</p>
			</div>

			<div className={styles.card}>
				<h3 className={styles.cardTitle}>Available Palettes</h3>
				{paletteState.palettes.length === 0 ? (
					<p className={styles.empty}>No palettes created yet.</p>
				) : (
					<div className={styles.paletteList}>
						{paletteState.palettes.map((palette) => {
							const indicator = getScoreIndicator(palette.baselineScore);
							return (
								<div key={palette.id} className={styles.paletteRow}>
									<div>
										{editingPaletteId === palette.id ? (
											<WeaveInput
												className={styles.paletteNameInput}
												type="text"
												value={editedPaletteName}
												onChange={(event: unknown) => {
													const target = event as {
														nativeEvent: { detail: { value: string } };
													};
													setEditedPaletteName(
														target.nativeEvent.detail.value
													);
												}}
												onBlur={savePaletteRename}
												onKeyDown={(
													event: React.KeyboardEvent<HTMLInputElement>
												) => {
													if (event.key === 'Enter') {
														savePaletteRename();
													}
													if (event.key === 'Escape') {
														cancelPaletteRename();
													}
												}}
											/>
										) : (
											<div
												className={styles.paletteName}
												onClick={() =>
													startPaletteRename(palette.id, palette.name)
												}
												title="Click to rename palette"
											>
												{palette.name}
											</div>
										)}
										<div className={styles.paletteMeta}>
											Score {palette.baselineScore} | Band {palette.band.min}-
											{palette.band.max}
										</div>
										<div className={styles.paletteIndicatorRow}>
											<span
												className={`${styles.scoreIndicator} ${indicator.className}`}
											>
												{indicator.label} impact
											</span>
										</div>
									</div>
									<WeaveButton
										variant="outlined"
										className={styles.deleteButton}
										onClick={() => handleDeletePalette(palette.id)}
									>
										Delete
									</WeaveButton>
								</div>
							);
						})}
					</div>
				)}
			</div>

			<div className={styles.card}>
				<h3 className={styles.cardTitle}>Function-Level Assignment</h3>
				<p className={styles.cardDescription}>{stageBehavior.functionHint}</p>
				{functionAssignments.length === 0 ? (
					<p className={styles.empty}>
						No function data available yet. Configure Functions & Spaces first.
					</p>
				) : (
					<div className={styles.assignmentList}>
						{functionAssignments.map((functionItem) => (
							<div key={functionItem.key} className={styles.assignmentRow}>
								<div className={styles.assignmentKey}>{functionItem.label}</div>
								<Select
									options={getPaletteOptions(paletteState.palettes)}
									value={paletteState.functionAssignments[functionItem.key] ?? ''}
									onChange={(event) =>
										updateFunctionAssignment(
											functionItem.key,
											event.detail.value
										)
									}
								/>
							</div>
						))}
					</div>
				)}
			</div>

			<div className={styles.card}>
				<h3 className={styles.cardTitle}>Area Assignment</h3>
				<p className={styles.cardDescription}>{stageBehavior.areaHint}</p>
				{stageBehavior.showAreaAssignments ? (
					areaAssignments.length === 0 ? (
						<p className={styles.empty}>No room areas found yet.</p>
					) : (
						<div className={styles.assignmentList}>
							{areaAssignments.map((area) => (
								<div key={area.key} className={styles.assignmentRow}>
									<div className={styles.assignmentKey}>{area.label}</div>
									<Select
										options={getPaletteOptions(paletteState.palettes)}
										value={
											paletteState.typologyAssignments[area.key] ??
											paletteState.typologyAssignments[area.legacyKey] ??
											''
										}
										onChange={(event) =>
											updateAreaAssignment(area.key, event.detail.value)
										}
									/>
								</div>
							))}
						</div>
					)
				) : null}
			</div>
		</div>
	);
};

export default MaterialsAssumptions;
